import { memo } from 'react';
import styled from 'styled-components';
import type { ProjectTemplate } from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import {
  AnimatedHeight,
  Box,
  Button2 as Button,
  Switch,
  Text,
  theme,
  Tooltip,
  TooltipToggle,
  useIsListBoxItemSelected,
} from '@frameio/vapor';
import {
  IconCollections,
  IconFolderClosed,
  IconMetadataFill,
  IconWarningFill,
} from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/TemplateRow';

type TemplateRowProps = {
  template: ProjectTemplate & {
    shouldIncludeAssets: boolean;
    setShouldIncludeAssets: (value: boolean) => void;
    fieldValidation?: {
      exceedsLimit: boolean;
    };
  };
};

export const TemplateRow = memo(function TemplateRow({
  template,
}: TemplateRowProps) {
  const isSelected = useIsListBoxItemSelected();

  return (
    <TemplateRowContents
      isBlankProject={false}
      isSelected={isSelected}
      template={template}
    />
  );
});

type TemplateRowContentsProps = TemplateRowProps & {
  isBlankProject: boolean;
  isSelected: boolean;
};

export const TemplateRowContents = ({
  isBlankProject,
  isSelected,
  template,
}: TemplateRowContentsProps) => {
  const fieldValidation = template.fieldValidation;
  const isDisabled = fieldValidation?.exceedsLimit ?? false;

  const isDetailedViewVisible = isSelected && template.name !== 'Blank';

  // Helper functions to reduce disabled logic repetition
  const getTextColor = (
    defaultColor: 'primary' | 'secondary' | 'body'
  ): 'disabled' | 'primary' | 'secondary' | 'body' =>
    isDisabled ? 'disabled' : defaultColor;

  const getIconColor = () =>
    isDisabled ? theme.colors.icon.interactive.off : undefined;
  const descriptionMaxLines = isSelected ? false : 3;
  const hasWarning = isDetailedViewVisible && isDisabled && !!fieldValidation;

  return (
    <TemplateRowWrapper $isDisabled={isDisabled}>
      <AnimatedHeight isShowing={hasWarning}>
        <Box padding={16}>
          <Box
            alignItems="center"
            backgroundColor="notice"
            borderRadius={6}
            display={'flex'}
            paddingX={6}
          >
            <Box
              alignItems="center"
              display="flex"
              gap={8}
              paddingX={8}
              paddingY={12}
            >
              <Box flexShrink="0">
                <IconWarningFill
                  color={theme.colors.icon.interactive.notice}
                  size={24}
                />
              </Box>
              <Text color="secondary" fontWeight="medium" variant="secondary">
                {TEXT.warningDescription}
              </Text>
            </Box>
            <Button size="small" variant="tertiary">
              {TEXT.warningButton}
            </Button>
          </Box>
        </Box>
      </AnimatedHeight>

      <TemplateMainContent hasWarning={hasWarning}>
        <TemplateThumbnail
          $backgroundImage={template.image ?? undefined}
          $isDisabled={isDisabled}
        />
        <TemplateDetails>
          <TemplateTitleAndDescription>
            <Text
              color={getTextColor('primary')}
              enableTooltip={true}
              fontWeight="medium"
              truncate={1}
              variant="body"
            >
              {isBlankProject ? TEXT.blankProject : template.name}
            </Text>
            <Text
              color={getTextColor('secondary')}
              truncate={descriptionMaxLines}
              variant="secondary"
            >
              {isBlankProject ? TEXT.startFromScratch : template.description}
            </Text>
          </TemplateTitleAndDescription>
        </TemplateDetails>
      </TemplateMainContent>

      <AnimatedHeight isShowing={isDetailedViewVisible}>
        <InfoTokenWrapper>
          <InfoToken>
            <IconMetadataFill color={getIconColor()} size={16} />
            <Text color={getTextColor('primary')} variant="caption">
              {TEXT.fields(template.customFieldCount || 0)}
            </Text>
          </InfoToken>
          <InfoToken>
            <IconCollections color={getIconColor()} size={16} />
            <Text color={getTextColor('primary')} variant="caption">
              {TEXT.collections(template.collectionCount || 0)}
            </Text>
          </InfoToken>
          <InfoToken>
            <IconFolderClosed color={getIconColor()} size={16} />
            <Text color={getTextColor('primary')} variant="caption">
              {TEXT.folders(template.folderCount || 0)}
            </Text>
          </InfoToken>
        </InfoTokenWrapper>
        <SampleContent>
          <SampleContentLabel>
            <Text color={getTextColor('body')} variant="secondary">
              {TEXT.includeSampleContent}
            </Text>
            {!isDisabled && (
              <Tooltip
                maxWidth="400px"
                placement="top"
                title={TEXT.sampleContentTooltipTitle}
              >
                <TooltipToggle
                  label={TEXT.sampleContentTooltipTitle}
                  size="small"
                />
              </Tooltip>
            )}
          </SampleContentLabel>

          <Switch
            checked={template.shouldIncludeAssets}
            disabled={isDisabled}
            label={TEXT.includeSampleContent}
            onChange={(e) => template.setShouldIncludeAssets(e.target.checked)}
            visuallyHideLabel={true}
          />
        </SampleContent>
      </AnimatedHeight>
    </TemplateRowWrapper>
  );
};

TEXT.DEFINE(
  {
    /** The title for creating a blank project */
    blankProject: 'Blank Project',
    /** Section label for blank project templates */
    startFromScratch: 'Start from scratch',
    /** Count label for collections included in the template */
    collections: (count: number) =>
      plural(count, {
        one: '1 Collection',
        other: `${count} Collections`,
      }),
    /** Count label for custom fields included in the template */
    fields: (count: number) =>
      plural(count, {
        one: '1 Field',
        other: `${count} Fields`,
      }),
    /** Count label for folders included in the template */
    folders: (count: number) =>
      plural(count, {
        one: '1 Folder',
        other: `${count} Folders`,
      }),
    /** Toggle label for including sample content in the new project */
    includeSampleContent: 'Include Sample Content',
    /** Button label for the template limit warning */
    warningButton: 'Learn more',
    /** Warning message when template exceeds account limits */
    warningDescription: `This template exceeds your account's custom field limit.`,
    /** Tooltip text describing sample content restrictions */
    sampleContentTooltipTitle:
      'Sample Content may not be used for any commercial purposes.',
  },
  { plural }
);

export const SampleContent = styled.div`
  align-items: center;
  border-top: 1px solid ${theme.colors.border.primary};
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  padding: 12px 16px;
`;

const SampleContentLabel = styled.div`
  align-items: center;
  column-gap: ${theme.spaces[4]};
  display: flex;
`;
const TemplateRowWrapper = styled.div<{
  $isDisabled?: boolean;
}>`
  display: flex;
  flex-direction: column;
  pointer-events: ${(props) => (props.$isDisabled ? 'none' : 'auto')};
`;

const TemplateDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TemplateThumbnail = styled.div<{
  $backgroundImage?: string;
  $isDisabled?: boolean;
}>`
  background-color: ${theme.colors.background.card};
  background-image: ${(p) => `url(${p.$backgroundImage ?? ''})`};
  background-size: cover;
  border-radius: ${theme.spaces[6]};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: calc(${theme.spaces['64']} + ${theme.spaces['8']});
  justify-content: center;
  outline: 2px solid ${theme.colors.border.secondary};
  position: relative;
  width: calc(${theme.spaces['64']} + ${theme.spaces['8']});
`;

const InfoTokenWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spaces[4]};
  padding-block-end: ${theme.spaces[16]};
  padding-inline-start: 104px;
  width: 100%;
`;

const InfoToken = styled.div`
  align-items: center;
  background-color: ${theme.colors.components.select.background};
  border-radius: 100px;
  display: flex;
  flex-wrap: nowrap;
  gap: ${theme.spaces[4]};
  padding: ${theme.spaces[4]} ${theme.spaces[8]};
`;

const TemplateMainContent = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'hasWarning',
})<{
  hasWarning?: boolean;
}>`
  display: flex;
  gap: ${theme.spaces[16]};
  padding: ${theme.spaces[16]};
  padding-top: ${({ hasWarning }) =>
    hasWarning ? theme.spaces[0] : theme.spaces[16]};
  position: relative;
  width: 100%;
`;

const TemplateTitleAndDescription = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${theme.spaces[4]};
  min-width: 0;
`;
